import SailboatOffshore16 from '@carbon/icons-react/es/sailboat--offshore/16';
import { Moment } from 'moment';
import React, { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { User } from 'api/models';
import { Collapse } from 'components/atoms/collapse';
import { Panel } from 'components/atoms/collapse/panel';
import { FormatTime } from 'components/organisms/timesheets/common/format-time';
import {
  getDays,
  getTotalMinutesFromEntries,
} from 'components/organisms/timesheets/common/utils';
import { Categories } from 'components/pages/timesheet-settings/categories';
import { Caption01, ProductiveHeading07 } from 'components/styles';
import { checkIfCurrentDateHasTimeOff } from 'utils/time-off-utils';
import { TimesheetTableTimeEntry, TimesheetWeeklyRow } from '../../types';
import { EntryDetail, SecondaryRow } from './entry-detail';

const StyledCollapse = styled(Collapse)`
  .ant-collapse-item .ant-collapse-header {
    padding: 0;
    border: 0;
    height: unset;
    margin-bottom: 1px;
  }
  .ant-collapse-item-disabled > .ant-collapse-header {
    color: rgba(0, 0, 0, 0.85);
  }
`;
const SecondaryText = styled.span`
  ${Caption01};
`;

const Header = styled.div`
  padding: var(--spacing-04) var(--spacing-05);
  background-color: var(--coolest-gray-20);
  display: flex;
  justify-content: space-between;
  ${ProductiveHeading07};
  width: 100%;

  ${SecondaryText} {
    margin-left: var(--spacing-02);
  }
  svg {
    color: var(--purple-60);
  }
`;

const TimeEntry = styled(EntryDetail)`
  margin-bottom: var(--spacing-01);

  &:last-child {
    margin-bottom: 0;

    ${SecondaryRow} {
      margin-bottom: 0;
    }
  }
`;

const DateWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

const PanelHeader = ({ date, minutes, isTimeOff }) => (
  <Header>
    <DateWrapper>
      {date.format('ddd')}
      <SecondaryText>{date.format('DD MMM')}</SecondaryText>
      {isTimeOff && (
        <span>
          <SailboatOffshore16 />
        </span>
      )}
    </DateWrapper>
    <div>
      <FormatTime value={minutes} showHypenIfZero={false} />
    </div>
  </Header>
);

interface Props {
  timesheet: TimesheetWeeklyRow;
  startDate: Moment;
  selectedDay?: string;
  categories: Categories;
  user: User;
}
export const Content: FC<Props> = ({
  timesheet,
  startDate,
  selectedDay,
  categories,
  user,
}) => {
  const entriesMap = useMemo(
    () =>
      getDays(startDate).map((x): [
        string,
        Moment,
        TimesheetTableTimeEntry[],
        number,
        boolean,
      ] => {
        const key = x.format('YYYY-MM-DD');
        const entries = timesheet.days[key] ?? [];
        const totalMinutes = getTotalMinutesFromEntries(entries);
        const isTimeOff = checkIfCurrentDateHasTimeOff(
          user?.userTimeOffDetails,
          x,
        );

        return [key, x, entries, totalMinutes, !!isTimeOff];
      }),
    [startDate, timesheet.days, user?.userTimeOffDetails],
  );

  const [activePanels, setActivePanel] = useState<string[]>([]);

  useEffect(() => {
    if (selectedDay) {
      setActivePanel(
        timesheet.days[selectedDay]?.length > 0 &&
          getTotalMinutesFromEntries(timesheet.days[selectedDay])
          ? [selectedDay]
          : [],
      );
    } else {
      setActivePanel(
        entriesMap
          .filter(
            ([_, _x, entries, totalMinutes]) =>
              entries.length > 0 && totalMinutes > 0,
          )
          .map(([a]) => a),
      );
    }
  }, [selectedDay, timesheet, entriesMap]);

  return (
    <StyledCollapse
      bordered={false}
      activeKey={activePanels}
      onChange={(panelKeys) => {
        setActivePanel(panelKeys as string[]);
      }}
    >
      {entriesMap.map(
        ([key, dateInMoment, entries, totalMinutes, isTimeOff]) => (
          <Panel
            showArrow={false}
            key={key}
            header={
              <PanelHeader
                date={dateInMoment}
                minutes={totalMinutes}
                isTimeOff={isTimeOff}
              />
            }
            collapsible={
              entries.length === 0 || totalMinutes === 0
                ? 'disabled'
                : undefined
            }
          >
            {(timesheet.days[key] ?? []).map((entry) => (
              <TimeEntry entry={entry} categories={categories} />
            ))}
          </Panel>
        ),
      )}
    </StyledCollapse>
  );
};
